import { useState, useEffect } from 'react';
import { Modal, Button, Label, TextInput, Alert } from 'flowbite-react';
import type { AddressResponse } from '../../types/api';

interface EditAddressModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (addressData: Partial<AddressResponse>) => Promise<void>;
  address?: AddressResponse | null;
  title?: string;
}

export const EditAddressModal: React.FC<EditAddressModalProps> = ({
  isOpen,
  onClose,
  onSave,
  address,
  title = 'Edit Address'
}) => {
  const [formData, setFormData] = useState<Partial<AddressResponse>>({
    streetNumberName: '',
    apartmentRoomSuiteNumber: '',
    city: '',
    stateDescriptorId: undefined,
    postalCode: '',
    nameOfCounty: '',
    isPrimary: false
  });
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (address) {
      setFormData({
        streetNumberName: address.streetNumberName || '',
        apartmentRoomSuiteNumber: address.apartmentRoomSuiteNumber || '',
        city: address.city || '',
        stateDescriptorId: address.stateDescriptorId,
        postalCode: address.postalCode || '',
        nameOfCounty: address.nameOfCounty || '',
        isPrimary: address.isPrimary || false
      });
    } else {
      // Reset for new address
      setFormData({
        streetNumberName: '',
        apartmentRoomSuiteNumber: '',
        city: '',
        stateDescriptorId: undefined,
        postalCode: '',
        nameOfCounty: '',
        isPrimary: false
      });
    }
    setError(null);
  }, [address, isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSaving(true);

    try {
      await onSave(formData);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save address');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal show={isOpen} onClose={onClose} dismissible size="xl" popup>
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <Alert color="failure" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div>
            <Label htmlFor="streetNumberName">Street Address *</Label>
            <TextInput
              id="streetNumberName"
              value={formData.streetNumberName || ''}
              onChange={(e) => setFormData({ ...formData, streetNumberName: e.target.value })}
              required
              placeholder="123 Main Street"
            />
          </div>

          <div>
            <Label htmlFor="apartmentRoomSuiteNumber">Apt/Suite/Room</Label>
            <TextInput
              id="apartmentRoomSuiteNumber"
              value={formData.apartmentRoomSuiteNumber || ''}
              onChange={(e) => setFormData({ ...formData, apartmentRoomSuiteNumber: e.target.value })}
              placeholder="Suite 100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="city">City *</Label>
              <TextInput
                id="city"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required
                placeholder="City"
              />
            </div>

            <div>
              <Label htmlFor="postalCode">Zip Code *</Label>
              <TextInput
                id="postalCode"
                value={formData.postalCode || ''}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                required
                placeholder="12345"
                maxLength={10}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="nameOfCounty">County</Label>
            <TextInput
              id="nameOfCounty"
              value={formData.nameOfCounty || ''}
              onChange={(e) => setFormData({ ...formData, nameOfCounty: e.target.value })}
              placeholder="County name"
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isPrimary"
              checked={formData.isPrimary}
              onChange={(e) => setFormData({ ...formData, isPrimary: e.target.checked })}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
            />
            <Label htmlFor="isPrimary">Set as primary address</Label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button color="light" onClick={onClose} disabled={isSaving}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : address ? 'Save Changes' : 'Add Address'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
};
